import {
  $,
  DatePicker,
  Disclosure,
  Header,
  HeaderMenuItem,
  Iterator,
  MonthSelector,
} from '@starberry/components';
import Select from 'react-select';
import { extendTheme, Tab, TabPanel, Text, Input } from '@chakra-ui/react';

// core providers
import providers from 'core/providers';
import DatePickerProvider from 'core/providers/DatePickerProvider';

// core components
import GlobalFormComponent from 'core/components/forms';
import SimpleTabs from 'core/components/SimpleTabs';
import InputField from 'core/components/InputField';
import PasswordField from 'core/components/PasswordField';
import FormDialog from 'core/components/FormDialog';

// base modules
import Dashboard from './modules/Dashboard';
import SideMenuItem from './modules/SideMenuItem';
import AccountHeader from './modules/AccountHeader';
import PropertyResults from './modules/PropertyResults';
import ActionableListItem from './modules/ActionableListItem';
import SectionHeading from './modules/SectionHeading';
import ContentSection from './modules/ContentSection';
import TwoColumn from './modules/2Column';
import CurrencyFormat from './modules/CurrencyFormat';
import MobileSideMenu from './modules/MobileSideMenu';
import DesktopSideMenu from './modules/DesktopSideMenu';
import LoginForm from './modules/LoginForm';
import ResetPassForm from './modules/ResetPassForm';
import ForgetPassForm from './modules/ForgetPassForm';
import SignUpForm from './modules/SignUpForm';
import DashboardHero from './modules/DashboardHero';
import MyLatestProperty from './modules/MyLatestProperty';
import PendingActions from './modules/PendingActions';
import ActivityHistory from './modules/ActivityHistory';
import SideMenu from './modules/SideMenu';
import ActionDialog from './modules/ActionDialog';
import AllProperties from './modules/AllProperties.jsx';
import Footer from './modules/Footer';
import FooterMenu from './modules/FooterMenu';
import PageHero from './modules/PageHero';
import ProfileForm from './modules/ProfileForm';
import ChangePasswordForm from './modules/ChangePasswordForm';
import PropertyItem from './modules/PropertyItem';
import HeaderMenu from './modules/HeaderMenu';
import UserPendingActionsText from './modules/UserPendingActionsText';
import ProfileHero from './modules/ProfileHero';
import MotionImage from './modules/MotionImage';
import RoundedCard from './modules/RoundedCard';
import Wishlist from './modules/Wishlist';
import ValuationButton from './modules/ValuationButton';
import MobileBottomNav from './modules/MobileBottomNav';
import SavedSearches from './modules/SavedSearches';
import ValuationCard from './modules/ValuationCard';
import SearchListItem from './modules/SearchListItem';
import NotificationPreferences from './modules/NotificationPreferences';
import Typography from './modules/base/Typography';
import Buttons from './modules/base/Buttons';
import NoContentAvailable from './modules/NoContentAvailable';
import WrappedLink from './modules/WrappedLink';
import PropertyRooms from './modules/PropertyRooms';
import Tooltip from './modules/Tooltip';
import OurServices from './modules/OurServices';
import VideoModule from './modules/VideoModule';
import Lightbox from './modules/Lightbox';
import BookViewingButton from './modules/property/BookViewingButton';
import TermsForm from './modules/TermsForm';

import LoginPage from './pages/LoginPage';
import SignUpPage from './pages/SignUpPage';
import ResetPassEmailPage from './pages/ResetPassEmailPage';
import ResetPassPage from './pages/ResetPassPage';
import DashboardPage from './pages/DashboardPage';
import MyPropertyPage from './pages/MyPropertyPage';
import MyPropertyBuyingPage from './pages/MyPropertyBuyingPage';
import ProfilePage from './pages/ProfilePage';
import InboxPage from './pages/InboxPage';
import CalendarPage from './pages/CalendarPage';
import NotFoundPage from './pages/NotFoundPage';

// base defaults
import translations from 'base/defaults/lang/en-GB/translations';
import sideNav from 'base/defaults/sidenav';
import CompanyLogo from 'base/defaults/CompanyLogo';
import { StarberryIcons } from 'base/defaults/icons';
import { default as forms } from 'base/defaults/forms';

export default {
  constants: {
    'site-name': 'Example',
    'site-logo': 'https://avatars.githubusercontent.com/u/3298033?s=200&v=4',
    'website-url': process.env.GATSBY_WEBSITE_URL,
    'base-url': process.env.GATSBY_MYACCOUNT_URL,
    propertyPageDetails: true,
    indexPage: 'DashboardPage',
    companyLogo: CompanyLogo,
    sideNav,
    ...translations,
    forms,
    icons: StarberryIcons,
    colors: {
      primary: '#20789C',
      primaryTextContrast: '#fff',
      secondary: '#C30E81',
      secondaryTextContrast: '#fff',
      tertiary: '#000',
      tertiaryTextContrast: '#fff',
      text: '#1C1C1C',
      button: '#3D3D3D',
      buttonPressed: '#5B5A5A',
      icons: '#232125',
      grey1: '#3C3C3B',
      grey2: '#575757',
      grey3: '#DDDDDD',
      grey4: '#F6F6F5',
      grey5: '#FAFAFA',
      grey6: '#BDBDBD',
    },
    sidebarBgColor: '#232125',
    headings: {
      baseHeading: {
        color: '#000',
      },
      headingSize: {
        h1: '4.125rem',
        h2: '3.125rem',
        h3: '2.25rem',
        h4: '1.75rem',
        h5: '1.375rem',
        h6: '1.375rem',
        text: '1rem',
      },
      headingLineHeight: {
        h1: 1.09,
        h2: 1.28,
        h3: 1.33,
        h4: 1.36,
        h5: 1.45,
        h6: 1.45,
      },
    },
    body: {
      bodySize: {
        xsmall: '0.6875rem',
        small: '0.875rem',
        medium: '1rem',
        large: '1.125rem',
        xlarge: '1.375rem',
      },
      bodyLineHeight: {
        xsmall: 1.63,
        small: 1.43,
        medium: 1.5,
        large: 1.44,
        xlarge: 1.55,
      },
    },
    caption: '0.6875rem',
    globalGridGap: [4, null, 8, null, 9],
  },
  providers: {
    ...providers,
    datepicker: DatePickerProvider,
  },
  theme: extendTheme({
    fonts: {
      body: 'Roboto',
      heading: 'Roboto',
    },
    lineHeights: {
      base: 1.5,
    },
    fontWeights: {
      bold: 700,
    },
    // components: {
    //   Button: {
    //     variants: {
    //       primary: (props) => ({
    //         color: props.colorMode === 'light' ? 'white' : 'gray.700',
    //         bgColor: props.colorMode === 'light' ? 'gray.700' : 'white',
    //         boxShadow: 'none',
    //         _hover: {
    //           color: props.colorMode === 'light' ? 'white' : 'gray.800',
    //           bgColor: props.colorMode === 'light' ? 'gray.800' : 'white',
    //           boxShadow: 'lg',
    //         },
    //       }),
    //     },
    //   },
    // },
  }),
  modules: {
    // Common Modules
    ...Typography,
    ...Buttons,
    Iterator: { component: Iterator },
    Header: { component: Header },
    HeaderMenuItem: { component: HeaderMenuItem },
    Dashboard: {
      $: Dashboard,
      companyLogo: $('constants.companyLogo'),
      sideNav: $('constants.sideNav'),
    },
    RoundedCard,
    Input: {
      component: Input,
      size: 'lg',
      fontSize: 'md',
      borderRadius: '3px',
      borderColor: '#898989',
      // focusBorderColor: '#000000',
      // errorBorderColor: 'red',
      _autofill: {
        bg: 'transparent',
      },
    },
    PasswordField: {
      component: PasswordField,
    },
    InputField: {
      component: InputField,
    },
    Select: {
      component: Select,
      props: {
        styles: {
          container: () => (provided) => ({
            ...provided,
            width: '100%',
            textAlign: 'left',
          }),
          menu: () => (provided) => ({
            ...provided,
            zIndex: 999999,
          }),
          control: () => (provided, state) => ({
            ...provided,
            height: '48px',
            borderRadius: 0,
            borderColor: state.isFocused ? '#000000' : '#898989',
            borderColor: state.isSelected ? '#000000' : '#000000',
            boxShadow: 'none',
          }),
        },
        options: {},
        onChange: () => {},
        defaultValue: undefined,
        value: undefined,
        name: undefined,
        label: undefined,
        variant: undefined,
        displayEmpty: undefined,
        type: undefined,
      },
    },
    Text: {
      component: Text,
      props: {
        color: $('theme.colors.text'),
      },
    },
    AccountHeader: {
      $: AccountHeader,
      companyLogo: $('constants.companyLogo'),
    },
    Disclosure: {
      component: Disclosure,
      props: ['render'],
    },
    GlobalFormComponent: {
      component: GlobalFormComponent,
    },
    HeaderMenu,
    CurrencyFormat,
    SideMenuItem,
    SideMenu,
    MobileSideMenu,
    DesktopSideMenu,
    MobileBottomNav,
    PropertyResults,
    SectionHeading,
    ActionableListItem,
    ContentSection,
    LoginForm,
    ResetPassForm,
    ForgetPassForm,
    SignUpForm,
    '2Column': TwoColumn,
    UserPendingActionsText,
    DashboardHero,
    MyLatestProperty,
    PendingActions,
    ActivityHistory,
    ActionDialog,
    AllProperties,
    ProfileForm,
    ChangePasswordForm,
    PropertyItem,
    SimpleTabs: {
      component: SimpleTabs,
      props: { tabs: [], sidecontent: '' },
    },
    MotionImage,
    PageHero,
    ProfileHero,
    Footer,
    FooterMenu,
    DatePicker: { component: DatePicker },
    MonthSelector: { component: MonthSelector },
    Wishlist,
    SavedSearches,
    ValuationButton,
    ValuationCard,
    SearchListItem,
    NotificationPreferences,
    NoContentAvailable,
    WrappedLink,
    PropertyRooms,
    Tooltip,
    OurServices,
    VideoModule,
    Lightbox,
    TermsForm,
    BookViewingButton,
    FormDialog: {
      component: FormDialog,
    },

    // TODO: Automatically pass all Chakra components propTypes to the 'props'
    TabPanel: { component: TabPanel, props: ['isSelected'] },
    Tab: { component: Tab, props: ['isSelected'] },

    // Page Modules
    LoginPage,
    SignUpPage,
    ResetPassEmailPage,
    ResetPassPage,
    DashboardPage,
    MyPropertyPage,
    MyPropertyBuyingPage,
    InboxPage,
    ProfilePage,
    CalendarPage,
    NotFoundPage,
  },
};
